'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import AvatarZoom from './AvatarZoom';
import ReviewerFaceImg from './ReviewerFaceImg';
import { fmtRating } from '@/lib/format';
import ReviewSlider, { ReviewSlide } from './ReviewSlider';
import { avatarImg } from '@/lib/imgUrl';
import { reviewCardName, reviewerCountLabel, reviewSourceOf } from '@/lib/reviewsPreview';
import ReviewerCountTip from './ReviewerCountTip';
import { SkeletonStyles } from './Skeletons';

import ReviewSourceLine from './ReviewSourceLine';

// Reviews V5 (Founder 30/09/26): full name (lib/reviewsPreview.ts reviewCardName), "(n)" reviewer Google count + tooltip, the SOURCE
// (Google / Facebook) + dd/MM/yy in the footer — never the reviewer city (an assigned value). rating NULL = Facebook (no stars).
export type ReviewItem = {
  reviewer_name: string; reviewer_avatar: string | null; rating: number | null; review_text: string | null; review_date: string;
  reviewer_name_full_he?: string | null; reviewer_name_full_he_kind?: string | null; original_reviewer_name?: string | null;
  reviewer_google_review_count?: number | null; review_source?: string | null; reviewer_gender?: string | null;
};

// Kept low on purpose — every review card ships an <img> avatar. Rendering all of a pro's
// reviews up front (was 16) was the biggest source of eager <img> requests on category pages
// (dozens of pros × 16 reviews = hundreds of images competing with everything else for
// bandwidth on first load). More reveal instantly on horizontal scroll (still-local data),
// then fetch further pages — see CategoryExpertsList's lazy-mount for the matching pro-card fix.
const INITIAL_VISIBLE = 6;
const REVEAL_BATCH = 10;
const FETCH_BATCH = 12;

// M3 (Founder 02/10/26): one review per slide in the shared ReviewSlider — the old column packing (2026-08 height budget) is gone.
const LINE_HEIGHT_PX = 20;
const CLAMP_LINES = 4;
// 2026-08-24 (user request + screenshot: a short review's card visibly ended with a lot
// of empty space while a long review's neighbor filled all the way down to its "קרא עוד"
// button, so two cards side by side looked mismatched/unfinished). TEXT_BLOCK_PX +
// FOOTER_PX are now FIXED per card regardless of actual text length (see ReviewCard's
// `<p>` and footer row below) — every card reserves the same vertical space a
// maximally-clamped 4-line review with a "קרא עוד" row would need, so a short review
// just leaves blank space WITHIN its own card instead of the card itself shrinking.
const TEXT_BLOCK_PX = CLAMP_LINES * LINE_HEIGHT_PX;
const FOOTER_PX = 18;
// Still needed to decide whether a review is long enough to need the "קרא עוד" toggle —
// the CARD's height is now fixed regardless (fixed TEXT_BLOCK_PX + FOOTER_PX), but a review
// that actually exceeds CLAMP_LINES still needs a way to read the rest of it.
function estimateLines(text: string | null, cap = CLAMP_LINES): number {
  const len = (text || '').trim().length;
  if (!len) return 1;
  return Math.min(cap, Math.max(1, Math.ceil(len / 40))); // ~40 chars per line on a ~270px slide (was 32 on the 208px column)
}

function ReviewCard({ r }: { r: ReviewItem }) {
  const name = reviewCardName(r);
  const src = reviewSourceOf(r.review_source);
  const countLabel = reviewerCountLabel(r.reviewer_google_review_count, src, name, r.reviewer_gender);
  const text = (r.review_text || '').trim();
  // Long reviews get an inline "קרא עוד" toggle that expands just this one
  // card — the rest of the row (and the contact column's height) is unaffected.
  const [expanded, setExpanded] = useState(false);
  const overflowing = text.length > 0 && estimateLines(text, 99) > CLAMP_LINES;

  return (
    <div className="rounded-xl p-2.5 flex flex-col" style={{ background: '#f8fafc', border: '1px solid #eef2f7', height: '100%' }}>
      {/* 2026-08-24 (user request, screenshot-driven redesign):
          - Rating moved to the far side (top-LEFT in RTL) — freeing up the space it used
            to take right after the name for a longer, less-truncated reviewer name.
          - Date moved OUT of this header row entirely, down to the footer row below
            (bottom-left) — see the footer past the review text. */}
      <div className="flex items-center gap-2 mb-1.5">
        {r.reviewer_avatar ? (
          <div className="flex-shrink-0">
            <ReviewerFaceImg src={r.reviewer_avatar} name={name} size={26} fontSize={12} className="rounded-full object-cover" style={{ cursor: 'zoom-in' }}
              wrap={img => <AvatarZoom src={r.reviewer_avatar as string} name={name}>{img}</AvatarZoom>} />
          </div>
        ) : (
          <div className="rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
            style={{ width: 26, height: 26, background: `hsl(${(name.charCodeAt(0) * 37) % 360},60%,42%)` }}>
            {name[0] || '?'}
          </div>
        )}
        <span className="flex items-baseline gap-1 flex-1 min-w-0">
          {/* bughunter 30/09: the name was cut with "…" at 390 on compact cards — it now wraps (up to 2 lines) so the full name reads */}
          <span className="text-sm font-bold min-w-0 leading-tight" style={{ color: '#0f172a', overflowWrap: 'anywhere' }}>{name}</span>
          {countLabel && <span className="flex-shrink-0"><ReviewerCountTip short={countLabel.short} tip={countLabel.tip} /></span>}
        </span>
        {r.rating != null && (
        <span className="flex items-center gap-1 flex-shrink-0 mr-auto">
          <span style={{ color: '#f59e0b', fontSize: 12 }}>★</span>
          <span className="text-sm font-bold" style={{ color: '#0d47a1' }}>{fmtRating(r.rating)}</span>
        </span>)}
      </div>
      {text ? (
        // 2026-08-24 (user request, screenshot-driven): fixed height regardless of the
        // actual review length — previously a short review's card visibly ended with a
        // lot of empty space while a longer neighbor filled all the way down to its "קרא
        // עוד" button, so two cards side by side looked mismatched. Now every card
        // reserves the SAME text-block height (TEXT_BLOCK_PX, the 4-line clamp's worth)
        // — a short review just leaves blank space within it instead of shrinking the
        // whole card.
        <p className="text-sm leading-snug" style={{
          color: '#475569',
          // Fixed height ONLY while collapsed — expanded ("הצג פחות" state) must grow to
          // fit the full text, so the fixed height only applies to the default/collapsed
          // state; minHeight (not height) once expanded keeps it from shrinking back
          // below the standard block if the full text happens to be shorter than it.
          [!expanded ? 'height' : 'minHeight']: TEXT_BLOCK_PX,
          // 2026-08-23 (user-caught bug): real line breaks in review text (paragraph
          // breaks a reviewer actually typed) were silently collapsed by the default
          // `white-space: normal` — this is also what makes a real newline actually
          // visible now that lib/reviewEngine.ts's unescapeLiteralNewlines() converts
          // a scraped literal "\n" into one.
          whiteSpace: 'pre-line',
          ...(!expanded ? { display: '-webkit-box', WebkitLineClamp: CLAMP_LINES, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' } : {}),
        }}>{text}</p>
      ) : (
        <p className="text-sm italic" style={{ color: '#cbd5e1', height: TEXT_BLOCK_PX }}>{r.rating != null ? 'דירוג ללא המלצה כתובה' : 'המלצה ללא טקסט'}</p>
      )}
      {/* Footer row — always rendered at the same height (FOOTER_PX) regardless of
          whether "קרא עוד" applies, so the fixed-height guarantee above holds for the
          WHOLE card, not just the text block. The empty first span (when there's no
          overflow) still occupies the "start" slot so justify-between reliably pushes
          the date to the opposite end (bottom-left in RTL) either way. */}
      <div className="flex items-center justify-between mt-1" style={{ minHeight: FOOTER_PX }}>
        <span>
          {overflowing && (
            <button onClick={() => setExpanded(v => !v)} className="text-xs font-bold" style={{ color: '#1d4ed8' }}>
              {expanded ? 'הצג פחות' : 'קרא עוד'}
            </button>
          )}
        </span>
        {/* V5 (30/09/26): RTL reading order = source NAME → icon → date (Founder 30/09/26, same order as the pro-page source line);
            the reviewer city is gone for good */}
        <ReviewSourceLine source={src} date={r.review_date} className="text-xs flex-shrink-0" />
      </div>
    </div>
  );
}

// the lazy next page of reviews → the shared placeholder (components/Skeletons.tsx), same box as a review column
// the lazy next page of reviews: a placeholder with the SAME box as a real slide (rv-slide width, stretched to the row height), so the swap to real
// cards moves nothing (15/10/26: it was a 208px stub that jumped to a ~326px card). No snap point of its own.
function ReviewSkeleton() {
  return (
    <div className="rv-slide" aria-hidden style={{ scrollSnapAlign: 'none', minHeight: 156 }}><SkeletonStyles />
      <div className="rounded-xl p-2.5" style={{ background: '#f8fafc', border: '1px solid #eef2f7', height: '100%', minHeight: 156 }}>
        <div className="flex items-center gap-2 mb-2"><div className="xs-bone" style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0 }} /><div className="xs-bone" style={{ width: 90, height: 11 }} /></div>
        <div className="xs-bone" style={{ height: 11, marginBottom: 6 }} /><div className="xs-bone" style={{ height: 11, marginBottom: 6 }} /><div className="xs-bone" style={{ height: 11, width: '70%' }} />
      </div>
    </div>
  );
}

// Horizontal reviews slider (M3: one review per slide in the shared ReviewSlider, the next one peeks);
// a long review can still be read in full via its own "קרא עוד" toggle. Scrolling
// to the end reveals more of what's already loaded, then lazy-fetches
// further pages for pros with many reviews. Shared by CategoryProCard (category
// pages) and HomeExperts (homepage) — one engine, not duplicated per surface.
export default function ReviewsList({ reviews, proId, totalCount }: { reviews: ReviewItem[]; proId: number; totalCount: number }) {
  const withText = useMemo(() => reviews.filter(r => r.review_text && r.review_text.trim()), [reviews]);
  const base = withText.length ? withText : reviews;

  const [allReviews, setAllReviews] = useState(base);
  const [visibleCount, setVisibleCount] = useState(Math.min(INITIAL_VISIBLE, base.length));
  const [loadingMore, setLoadingMore] = useState(false);
  const initialCount = useRef(base.length).current;   // slides beyond the first page fade in (opacity only); the first page never flashes
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const canRevealLocally = visibleCount < allReviews.length;
  const canFetchMore = allReviews.length < totalCount;
  const canLoadMore = canRevealLocally || canFetchMore;

  // M3: touch is NATIVE now (scroll-snap needs the browser's own pan; the old JS axis-lock driver set scrollLeft by hand, which
  // fights snapping) — horizontal swipe = the slider, vertical = the page. Mouse drag = useDragScroll inside ReviewSlider.

  useEffect(() => {
    const el = sentinelRef.current;
    const root = containerRef.current;
    if (!el || !root || !canLoadMore) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      if (visibleCount < allReviews.length) {
        setVisibleCount(v => Math.min(v + REVEAL_BATCH, allReviews.length));
        return;
      }
      if (loadingMore || !proId || allReviews.length >= totalCount) return;
      setLoadingMore(true);
      fetch(`/api/professionals/${proId}/reviews?offset=${allReviews.length}&limit=${FETCH_BATCH}`)
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
          if (data?.reviews?.length) {
            // warm the tap-to-enlarge faces of the new page (exact zoom candidate) so the first tap on them is instant too
            try { for (const r of data.reviews) { const a = r?.reviewer_avatar ? avatarImg(r.reviewer_avatar, 280) : null; if (a?.src && !a.src.includes('ui-avatars.com')) { const im = new Image(); if (a.srcSet) im.srcset = a.srcSet; im.src = a.src; } } } catch { /* best effort */ }
            setAllReviews(prev => [...prev, ...data.reviews]);
            setVisibleCount(v => v + data.reviews.length);
          }
        })
        .catch(() => {})
        .finally(() => setLoadingMore(false));
    }, { root, rootMargin: '0px 240px 0px 240px', threshold: 0.01 });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCount, allReviews.length, loadingMore, proId, totalCount, canLoadMore]);

  const shownReviews = allReviews.slice(0, visibleCount);
  if (!shownReviews.length) return null;

  return (
    <ReviewSlider proId={proId} scrollRef={containerRef} className="md:flex-1 md:min-h-0" style={{ alignItems: 'stretch' }}>
      <style>{'@keyframes rvIn{from{opacity:0}to{opacity:1}}'}</style>
      {shownReviews.map((r, ri) => <ReviewSlide key={ri} style={ri >= initialCount ? { animation: 'rvIn .2s ease both' } : undefined}><ReviewCard r={r} /></ReviewSlide>)}
      {loadingMore && <ReviewSkeleton />}
      {canLoadMore && <div ref={sentinelRef} aria-hidden className="flex-shrink-0" style={{ width: 1, alignSelf: 'stretch' }} />}
    </ReviewSlider>
  );
}
